import {Component, OnInit, HostListener} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Title} from '@angular/platform-browser';
import {MenuItem, MessageService} from 'primeng/api';
import {NavigationService} from '../../../../service/navigation.service';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {AccountService} from '../../../../service/account.service';
import {AccountInterface} from '../../../../model/account.interface';
import {ConversionEngineSettingsService} from '../../../../service/conversion-engine-settings.service';
import {ConversionEngineSettings} from '../../../../model/conversion-engine-settings.model';

@Component({
  selector: 'auto-conversion-engine',
  templateUrl: './conversion-engine.component.html',
  styleUrls: ['./conversion-engine.component.scss']
})
export class ConversionEngineComponent implements OnInit {
  accountId: number;
  settingsSideNav: MenuItem[];
  account: AccountInterface;
  privateOffersForm: UntypedFormGroup;
  vdpOffersForm: UntypedFormGroup;
  conversionSettings: ConversionEngineSettings | null = null;
  isSaving = false;

  constructor(
    private titleService: Title,
    private route: ActivatedRoute,
    protected formBuilder: UntypedFormBuilder,
    private navigationService: NavigationService,
    protected messageService: MessageService,
    protected accountService: AccountService,
    private conversionEngineSettingsService: ConversionEngineSettingsService,
  ) {
  }

  async ngOnInit(): Promise<void> {
    this.titleService.setTitle('Conversion Engine');
    this.route.paramMap.subscribe((params) => {
      const idParam = params.get('accountId');
      if (idParam) {
        this.accountId = parseInt(idParam);
        this.initNavigation();
        this.loadSettings();
      }
    });
  }

  /**
   * Initialize the side navigation menu
   */
  initNavigation(): void {
    this.navigationService
      .getMenuItems(this.accountId, 'settings-view-side-menu.configuration.json')
      .then((navItems) => (this.settingsSideNav = navItems));
  }

  /**
   * Load account and conversion engine settings
   */
  private loadSettings(): void {
    this.accountService.getAccount(this.accountId).subscribe({
      next: (account) => {
        this.account = account;
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load account'
        });
      }
    });

    this.conversionEngineSettingsService.getSettings(this.accountId).subscribe({
      next: (settings) => {
        this.conversionSettings = settings;
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load settings'
        });
      }
    });
  }

  /**
   * Initialize the private offers form group
   */
  onPrivateOffersFormGroupInitialized(form: UntypedFormGroup) {
    this.privateOffersForm = form;
  }

  /**
   * Initialize the VDP offers form group
   */
  onVdpOffersFormGroupInitialized(form: UntypedFormGroup) {
    this.vdpOffersForm = form;
  }

  /**
   * Check if the save button should be disabled
   */
  getSaveButtonDisabled(): boolean {
    let isDisabled = true;
    if (this.privateOffersForm) {
      if (this.privateOffersForm.dirty && this.privateOffersForm.valid) {
        isDisabled = false;
      }
    }
    if (this.vdpOffersForm) {
      if (this.vdpOffersForm.dirty && this.vdpOffersForm.valid) {
        isDisabled = false;
      }
    }
    return isDisabled;
  }

  /**
   * Save conversion engine settings
   */
  saveSettings(): void {
    if (this.conversionSettings) {
      this.isSaving = true;
      const payload: ConversionEngineSettings = {
        id: this.conversionSettings.id,
        accountId: this.accountId,
        includePrivateOffersInExternalAdvertising: !!this.privateOffersForm?.get('includePrivateOffersInExternalAdvertising')?.value,
        unlockPrivateOffersEnabled: !!this.privateOffersForm?.get('unlockPrivateOffersEnabled')?.value,
        vdpOfferWidgetEnabled: !!this.vdpOffersForm?.get('vdpOfferWidgetEnabled')?.value
      };

      this.conversionEngineSettingsService.updateSettings(payload).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Saved',
            detail: 'Settings updated'
          });
          if (this.privateOffersForm) {
            this.privateOffersForm.markAsPristine();
          }
          if (this.vdpOffersForm) {
            this.vdpOffersForm.markAsPristine();
          }
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to save settings'
          });
          this.isSaving = false;
        },
        complete: () => {
          this.isSaving = false;
        }
      });
    }
  }

  /**
   * Show native prompt on browser/tab refresh or close
   */
  @HostListener('window:beforeunload', ['$event'])
  confirmOnBrowserClose(event: BeforeUnloadEvent): void {
    let shouldPrompt = false;
    if (this.privateOffersForm) {
      if (this.privateOffersForm.dirty) {
        shouldPrompt = true;
      }
    }
    if (this.vdpOffersForm) {
      if (this.vdpOffersForm.dirty) {
        shouldPrompt = true;
      }
    }
    if (shouldPrompt) {
      event.returnValue = true;
    }
  }
}
